import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { kaGenerationApi, extractApiError, formatValidationError } from '@/lib/api'
import { useToast } from './useToast'
import type {
  CreateKAGenDto,
  UpdateKAGenDto,
  AllocateKAGenDto,
  RegenerateKAGenDto,
  KAGenDocument,
  KAGenDocSummary,
  KAGenAllocationResult,
} from '@/types'

// ── List ────────────────────────────────────────────────────────────

export function useKAGenDocs(page = 1, perPage = 50) {
  return useQuery({
    queryKey: ['ka-generation', page, perPage],
    queryFn: async () => {
      const res = await kaGenerationApi.getAll({ page, per_page: perPage })
      const raw = res.data as { data?: KAGenDocSummary[]; total?: number }
      return {
        docs: (raw.data ?? []) as KAGenDocSummary[],
        total: (raw.total ?? 0) as number,
      }
    },
  })
}

// ── Single document ─────────────────────────────────────────────────

export function useKAGenDoc(id: string | undefined) {
  return useQuery({
    queryKey: ['ka-generation', id],
    queryFn: async () => {
      if (!id) return null
      const res = await kaGenerationApi.getById(id)
      return res.data as KAGenDocument
    },
    enabled: !!id,
  })
}

// ── Create ──────────────────────────────────────────────────────────

export function useCreateKAGen() {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateKAGenDto) => kaGenerationApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ka-generation'] })
      toast.success('Документ создан')
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err) || 'Ошибка создания документа')
    },
  })
}

// ── Update ──────────────────────────────────────────────────────────

export function useUpdateKAGen(id: string) {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: UpdateKAGenDto) => kaGenerationApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ka-generation'] })
      toast.success('Документ обновлён')
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err) || 'Ошибка обновления')
    },
  })
}

// ── Delete ──────────────────────────────────────────────────────────

export function useDeleteKAGen() {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => kaGenerationApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ka-generation'] })
      toast.success('Документ удалён')
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err) || 'Ошибка удаления')
    },
  })
}

// ── Generate codes ──────────────────────────────────────────────────

export function useGenerateKACodes(id: string) {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: () => kaGenerationApi.generate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ka-generation', id] })
      qc.invalidateQueries({ queryKey: ['ka-generation'] })
      toast.success('КА-коды сгенерированы')
    },
    onError: (err: unknown) => {
      toast.error(formatValidationError(err) || 'Ошибка генерации')
    },
  })
}

// ── Load from file ──────────────────────────────────────────────────

export function useLoadKAFromFile(id: string) {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (formData: FormData) => kaGenerationApi.loadFromFile(id, formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ka-generation', id] })
      qc.invalidateQueries({ queryKey: ['ka-generation'] })
      toast.success('КА-коды загружены из файла')
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err) || 'Ошибка загрузки файла')
    },
  })
}

// ── Codes (paginated) ───────────────────────────────────────────────

export function useKAGenCodes(
  id: string | undefined,
  params?: { type?: string; page?: number; per_page?: number; pallet_num?: number },
) {
  return useQuery({
    queryKey: ['ka-generation-codes', id, params],
    queryFn: async () => {
      if (!id) return null
      const res = await kaGenerationApi.getCodes(id, params)
      return res.data as {
        ka_boxes?: Array<{ num: number; sscc: string; ka: string; ka_barcode: string; pallet_num?: number }>
        ka_boxes_total?: number
        ka_pallets?: Array<{ num: number; sscc: string; ka: string; ka_barcode: string; boxes_count?: number }>
        ka_pallets_total?: number
      }
    },
    enabled: !!id,
  })
}

// ── Allocate to task ────────────────────────────────────────────────

export function useAllocateKAGen(id: string) {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: AllocateKAGenDto) => kaGenerationApi.allocate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ka-generation', id] })
      qc.invalidateQueries({ queryKey: ['ka-generation'] })
      toast.success('Агрегаты назначены на задание')
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err) || 'Ошибка назначения агрегатов')
    },
  })
}

// ── Regenerate ──────────────────────────────────────────────────────

export function useRegenerateKAGen(id: string) {
  const qc = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data?: RegenerateKAGenDto) => kaGenerationApi.regenerate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ka-generation', id] })
      qc.invalidateQueries({ queryKey: ['ka-generation'] })
      toast.success('Агрегаты перегенерированы')
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err) || 'Ошибка перегенерации')
    },
  })
}

// ── Allocation (query) ──────────────────────────────────────────────

export function useKAGenAllocation(docId: string | undefined, taskId: string | undefined) {
  return useQuery({
    queryKey: ['ka-generation-allocation', docId, taskId],
    queryFn: async () => {
      if (!docId || !taskId) return null
      const res = await kaGenerationApi.getAllocation(docId, taskId)
      return res.data as KAGenAllocationResult
    },
    enabled: !!docId && !!taskId,
  })
}
